// Stale-while-revalidate cache for callable-backed reads.
//
// The heavy dashboard/admin pages are powered by Cloud Functions. Even warm,
// a round-trip costs 300ms-1.5s; cold it's several seconds. This helper makes
// repeat page loads paint instantly from the last result (localStorage) and
// then silently refresh from the network, applying fresh data when it lands.
//
// Cache keys MUST be scoped to the signed-in identity (uid or advertiserId)
// and include all query args, so account switches and filter changes never
// show someone else's (or some other range's) numbers.

const PREFIX = 'swr:';

interface CacheEntry<T> {
  t: number; // epoch ms when stored
  v: T;
}

function read<T>(key: string, ttlMs: number): CacheEntry<T> | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry<T>;
    if (!entry || typeof entry.t !== 'number') return null;
    if (Date.now() - entry.t > ttlMs) return null;
    return entry;
  } catch {
    return null;
  }
}

function write<T>(key: string, value: T) {
  const entry: CacheEntry<T> = { t: Date.now(), v: value };
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(entry));
  } catch {
    // Quota exceeded — drop all swr entries and retry once. Cache is an
    // optimization; losing it must never break the page.
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith(PREFIX)) localStorage.removeItem(k);
      }
      localStorage.setItem(PREFIX + key, JSON.stringify(entry));
    } catch {
      /* give up silently */
    }
  }
}

/** Drop every swr cache entry — call on sign-out. */
export function clearSwrCache() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) localStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}

export interface SwrApplyMeta {
  fromCache: boolean;
  fetchedAt: number;
}

/**
 * Apply cached data immediately (if fresh enough), then fetch and apply the
 * network result. `apply` runs once with the cached value (synchronously,
 * when present) and once more when the fetch lands. If the fetch fails but
 * cache was applied, the error is swallowed (logged) so the user keeps the
 * stale-but-usable view; with no cache the error propagates to the caller's
 * catch as before.
 */
export async function swrFetch<T>(opts: {
  key: string;
  ttlMs: number;
  fetcher: () => Promise<T>;
  apply: (value: T, meta: SwrApplyMeta) => void;
}): Promise<void> {
  const cached = read<T>(opts.key, opts.ttlMs);
  if (cached) {
    opts.apply(cached.v, { fromCache: true, fetchedAt: cached.t });
  }
  try {
    const fresh = await opts.fetcher();
    write(opts.key, fresh);
    opts.apply(fresh, { fromCache: false, fetchedAt: Date.now() });
  } catch (err) {
    if (cached) {
      console.warn(`[swr] background refresh failed for ${opts.key}`, err);
      return;
    }
    throw err;
  }
}

/** Standard TTLs. Reports tolerate hours of staleness; money state does not. */
export const TTL = {
  wallet: 2 * 60 * 1000, // 2 min — balance must track deposits/clicks closely
  report: 3 * 60 * 60 * 1000, // 3 h — historical analytics drift slowly
  adminList: 3 * 60 * 60 * 1000, // 3 h — instant paint; background refresh corrects
} as const;
